// Generated by GitHub Copilot
import i18next from 'i18next'
import { detectDeviceLanguage, storeLanguage } from '#i18n.ts'
import { State } from '#core/state/State.ts'
import GeneralSettings from '#core/entities/auth/users/GeneralSettings.ts'
import UserSettingsApi from '#core/api/auth/UserSettingsApi.ts'
import { Displayable } from '#core/entities/shared/BaseModels.ts'

/**
 * Global in-memory state for the authenticated user's general settings (language, date format, time format).
 * Use `useGlobalState(UserGeneralSettings)` to subscribe in components.
 * Settings are loaded automatically on instantiation via `_onLoaded`.
 * Call `save()` to persist the current values back to the backend.
 */
export class UserGeneralSettings extends State<UserGeneralSettings> {
    language: string = detectDeviceLanguage();
    dateFormat: string = 'DD-MM-YYYY';
    timeFormat: string = '24h';
    /** IANA timezone the user picked; null follows the browser. */
    timeZone: string | null = null;

    $isLoading: boolean = false;
    $isSaving: boolean = false;
    $saveError: string | null = null;

    private readonly _api = new UserSettingsApi();

    constructor(key: string | undefined, storage: Storage | null) {
        super(key, storage);
        // Propagate language changes immediately to i18next regardless of how the field is set.
        this.onSet('language', () => {
            if (i18next.isInitialized && i18next.language !== this.language) {
                void i18next.changeLanguage(this.language);
            }
        });
        // Propagate format changes to Displayable so entity formatDate/formatDateTime stay current.
        this.onSet('dateFormat', () => this._pushFormats());
        this.onSet('timeFormat', () => this._pushFormats());
        // Field initializers bypass onSet, so push the defaults before the request is in flight.
        this._pushFormats();
        void this._load();
    }
   

    private async _load(): Promise<void> {
        this.reactTo.$isLoading = true;
        this.reactTo.$saveError = null;
        try {
            const result = await this._api.getGeneralSettings();
            if (result) {
                this.reactTo.language = result.language || detectDeviceLanguage();
                if (result.date_format) this.reactTo.dateFormat = result.date_format;
                if (result.time_format) this.reactTo.timeFormat = result.time_format;
                if (result.timezone) this.reactTo.timeZone = result.timezone;
            }
            // The user's setting wins over whatever a public page left i18next on, and is what the
            // browser remembers — even when it equals the default and onSet never fired.
            storeLanguage(this.language);
            if (i18next.isInitialized && i18next.language !== this.language) {
                void i18next.changeLanguage(this.language);
            }
        } catch {
            // Nothing to surface: the field defaults stay in effect, so the fire-and-forget
            // call in the constructor must not leak the rejection.
        } finally {
            // Even on a failed or empty fetch, Displayable needs the defaults instead of its Intl fallback.
            this._pushFormats();
            this.reactTo.$isLoading = false;
        }
    }

    /** Forwards current format preferences to `Displayable` for use in entity formatting methods. */
    private _pushFormats(): void {
        Displayable.setFormatPreferences(this.dateFormat, this.timeFormat);
    }

    /** Persists the current general settings to the API. */
    async save(): Promise<void> {
        this.reactTo.$isSaving = true;
        this.reactTo.$saveError = null;
        try {
            const entity = new GeneralSettings();
            entity.language = this.language;
            entity.date_format = this.dateFormat;
            entity.time_format = this.timeFormat;
            entity.timezone = this.timeZone;
            await this._api.updateGeneralSettings(entity);
        } catch {
            this.reactTo.$saveError = 'save_failed';
        } finally {
            this.reactTo.$isSaving = false;
        }
    }
}
